import type { AuthStatus, Feature, PublicUser } from "@codvero/shared";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, Outlet, useNavigate, useRouterState } from "@tanstack/react-router";
import { Bot, Home, LogOut, Monitor, Settings, X, MessageCircle } from "lucide-react";
import { projectsApi } from "@/api/projects.ts";
import { LogoMark } from "@/ui/Logo.tsx";
import { useCallback, useEffect, useRef, useState } from "react";
import { useDevice, useEdgeSwipe } from "@/lib/media.ts";
import { useTranslation } from "react-i18next";
import { authApi } from "@/api/auth.ts";
import { authStatusQuery, useAppName } from "@/features/auth/session.ts";
import { CreateProjectDialog } from "@/features/projects/CreateProjectDialog.tsx";
import { cn } from "@/lib/cn.ts";
import { useConfirm } from "@/ui/ConfirmDialog.tsx";
import { SpaceSidebar } from "@/features/space/SpacePage.tsx";
import { ProjectsSidebar } from "./ProjectsSidebar.tsx";

const SIDEBAR_KEY = "codvero.sidebar.open";
/** iPad keeps its own preference: the list pushes the content there, so it starts closed. */
const TABLET_SIDEBAR_KEY = "codvero.sidebar.open.tablet";
const readFlag = (key: string, fallback: boolean) => {
  try {
    const v = localStorage.getItem(key);
    return v === null ? fallback : v !== "0";
  } catch {
    return fallback;
  }
};

/** Three-zone layout: icon rail, projects sidebar, main column (the dock lives inside project pages). */
const railBtn = "relative grid size-[38px] place-items-center rounded-[10px] text-fg-muted transition hover:bg-surface-2 hover:text-fg";
/** Module level: a component defined during render would be a new type each render and remount. */
function RailLink({ to, icon: Icon, label, exact }: { to: string; icon: typeof Home; label: string; exact?: boolean }) {
  return (
    <Link to={to} activeOptions={{ exact: !!exact }} title={label} aria-label={label} className={railBtn} activeProps={{ className: "bg-accent-soft text-accent" }}>
      <Icon className="size-[18px]" />
    </Link>
  );
}

export function AppShell({ user }: { user: PublicUser }) {
  const { t } = useTranslation();
  const appName = useAppName();
  // the tab's title tells Codvero Lab from the main platform too
  useEffect(() => { document.title = appName; }, [appName]);
  const qc = useQueryClient();
  const nav = useNavigate();
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const device = useDevice();
  const { i18n } = useTranslation();
  const rtl = i18n.dir() === "rtl";
  // the Chat section swaps the projects sidebar for its own conversations list
  const inSpace = pathname === "/chat" || pathname.startsWith("/chat/");
  // only what the account may use is offered (the server refuses the rest anyway)
  const can = (f: Feature) => user.features.includes(f);
  useEffect(() => {
    const home = can("containers") ? "/" : can("chat") ? "/chat" : can("studio") ? "/agents" : "/settings";
    const blocked = (pathname === "/" && !can("containers")) || (pathname.startsWith("/agents") && !can("studio")) || (inSpace && !can("chat")) || (/^\/projects(\/|$)/.test(pathname) && !can("containers"));
    if (blocked && pathname !== home) void nav({ to: home, replace: true });
  }, [pathname, user.features.join(",")]);
  const [desktopSidebar, setDesktopSidebar] = useState(() => readFlag(SIDEBAR_KEY, true));
  const [tabletSidebar, setTabletSidebar] = useState(() => readFlag(TABLET_SIDEBAR_KEY, false));
  const sidebar = device === "tablet" ? tabletSidebar : desktopSidebar;
  const setSidebar = (v: boolean) => {
    if (device === "tablet") setTabletSidebar(v);
    else setDesktopSidebar(v);
    try {
      localStorage.setItem(device === "tablet" ? TABLET_SIDEBAR_KEY : SIDEBAR_KEY, v ? "1" : "0");
    } catch {
      /* ignore */
    }
  };
  const [mobileSidebar, setMobileSidebar] = useState(false);
  const [mobileProjects, setMobileProjects] = useState(false);
  const mobileShowsProjects = !inSpace || mobileProjects;
  // the computer icon shows the containers (projects) list in the sidebar from any section, the Chat section too
  const [projectsMode, setProjectsMode] = useState(false);
  useEffect(() => setProjectsMode(false), [inSpace]);
  const showProjects = !inSpace || projectsMode;
  /** ⌘\ : the current section's own list (chats in the Chat section, else the containers) */
  const togglePanel = () => {
    if (sidebar && showProjects && inSpace) return setProjectsMode(false);
    setProjectsMode(false);
    setSidebar(!sidebar);
  };
  /** the computer icon: the containers list, open or closed */
  const toggleContainers = () => {
    if (sidebar && showProjects) return setSidebar(false);
    setProjectsMode(true);
    setSidebar(true);
  };
  /** the chat icon, the same way: opens the Chat section with its conversations list; open, it closes the list */
  const toggleChats = () => {
    setProjectsMode(false);
    if (!inSpace) {
      setSidebar(true);
      void nav({ to: "/chat" });
      return;
    }
    setSidebar(!(sidebar && !showProjects));
  };
  const [creating, setCreating] = useState(false);
  useEffect(() => { setMobileSidebar(false); setMobileProjects(false); }, [pathname]);
  // phone: swipe in from the leading edge opens the projects drawer (right edge in Arabic)
  const openDrawer = useCallback(() => setMobileSidebar(true), []);
  useEdgeSwipe(device === "phone" && !mobileSidebar, rtl ? "right" : "left", openDrawer);
  const drawerTouch = useRef<{ x: number; y: number } | null>(null);
  const [shortcutsOpen, setShortcutsOpen] = useState(false);

  // Keyboard shortcuts (iPad with a keyboard, desktop). ⌘ on Apple devices, Ctrl elsewhere.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const mod = e.metaKey || e.ctrlKey;
      const typing = (e.target as HTMLElement | null)?.closest?.("input, textarea, [contenteditable=true], .xterm");
      if (mod && e.key.toLowerCase() === "k") {
        // search: the page's own search when it has one, else the projects list
        e.preventDefault();
        const pageSearch = document.querySelector<HTMLInputElement>("[data-global-search]");
        if (pageSearch) return pageSearch.focus();
        if (device === "phone") setMobileSidebar(true);
        else setSidebar(true);
        setTimeout(() => document.querySelector<HTMLInputElement>("[data-sidebar-search]")?.focus(), 50);
      } else if (mod && e.key === "\\") {
        e.preventDefault();
        if (device === "phone") setMobileSidebar((v) => !v);
        else togglePanel();
      } else if (mod && e.key === "/") {
        e.preventDefault();
        setShortcutsOpen((v) => !v);
      } else if (mod && e.key === ",") {
        e.preventDefault();
        void nav({ to: "/settings" });
      } else if (!typing && e.key === "?" && !mod) {
        setShortcutsOpen(true);
      } else if (e.key === "Escape") {
        setShortcutsOpen(false);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  });

  const logout = useMutation({
    mutationFn: authApi.logout,
    onSettled: () => {
      qc.setQueryData<AuthStatus>(authStatusQuery.queryKey, (old) => ({ setupRequired: false, user: null, googleSignIn: false, singleProject: old?.singleProject ?? false, edition: old?.edition ?? null }));
      void nav({ to: "/login" });
    },
  });

  const { confirm } = useConfirm();
  const askLogout = async () => {
    if (await confirm({ title: t("common.signOut"), message: t("confirm.signOutMsg"), confirmLabel: t("common.signOut"), danger: true })) logout.mutate();
  };

  // The projects button carries a badge with the number of projects that need attention (error state).
  const projects = useQuery({ queryKey: ["projects"], queryFn: projectsApi.list, staleTime: 30_000 });
  const attention = (projects.data ?? []).filter((p) => p.state === "error").length;

  return (
    <div className="flex h-full flex-col md:flex-row">
      {/* rail (iPad and desktop) */}
      <aside className="pt-window hidden w-14 shrink-0 flex-col items-center gap-1.5 border-e border-border bg-surface pb-3 md:flex">
        <Link to="/" title={t("nav.home")} aria-label={t("nav.home")} className="mb-1 grid size-[46px] place-items-center rounded-[14px] hover:bg-surface-2">
          <LogoMark size={36} />
        </Link>
        {can("containers") && (
          <button type="button" title={t("nav.containers")} aria-label={t("nav.containers")} onClick={toggleContainers} className={cn(railBtn, sidebar && showProjects && "bg-accent-soft text-accent")}>
            <Monitor className="size-[18px]" />
            {attention > 0 && <span className="absolute -top-0.5 -end-0.5 grid min-w-[14px] place-items-center rounded-full bg-danger px-1 text-[9px] font-semibold leading-[14px] text-white" title={t("nav.attention", { count: attention })}>{attention}</span>}
          </button>
        )}
        {can("studio") && <RailLink to="/agents" icon={Bot} label={t("nav.agents")} />}
        {can("chat") && (
          <button type="button" title={t("nav.chat")} aria-label={t("nav.chat")} onClick={toggleChats} className={cn(railBtn, inSpace && (sidebar && !showProjects ? "bg-accent-soft text-accent" : "text-accent"))}>
            <MessageCircle className="size-[18px]" />
          </button>
        )}
        <div className="mt-auto flex flex-col items-center gap-1.5">
          <RailLink to="/settings" icon={Settings} label={t("nav.settings")} />
          <button type="button" title={t("common.signOut")} aria-label={t("common.signOut")} onClick={() => void askLogout()} className={railBtn}><LogOut className="size-[18px]" /></button>
        </div>
      </aside>

      {/* sidebar (iPad: pushes the content; desktop) */}
      {/* the containers list only for an account that has containers */}
      {sidebar && (!showProjects || can("containers")) && (
        <aside className="hidden w-64 shrink-0 border-e border-border bg-surface md:block">
          <div className="flex items-center justify-between px-4 pt-4">
            <div>
              <div className="font-semibold leading-tight">{appName}</div>
              <div className="text-[11px] text-fg-muted">{showProjects ? t("nav.projects") : t("nav.chat")}</div>
            </div>
          </div>
          <div className="h-[calc(100%-3.25rem)]">
            {showProjects ? <ProjectsSidebar onNewProject={() => setCreating(true)} /> : <SpaceSidebar />}
          </div>
        </aside>
      )}

      {/* phone: projects drawer (swipe back toward the edge to close) */}
      {mobileSidebar && (!mobileShowsProjects || can("containers")) && (
        <div className="fixed inset-0 z-40 flex md:hidden">
          <div
            className="flex h-full w-80 max-w-[88vw] flex-col bg-surface pt-safe shadow-xl"
            onTouchStart={(e) => { const p = e.touches[0]; drawerTouch.current = p ? { x: p.clientX, y: p.clientY } : null; }}
            onTouchEnd={(e) => {
              const p = e.changedTouches[0];
              const st = drawerTouch.current;
              drawerTouch.current = null;
              if (!p || !st) return;
              const dx = (p.clientX - st.x) * (rtl ? 1 : -1);
              if (dx > 70 && Math.abs(p.clientY - st.y) < 60) setMobileSidebar(false);
            }}
          >
            <div className="flex items-center justify-between px-4 pt-4">
              <div className="flex items-center gap-2"><LogoMark size={28} /><div className="font-semibold">{mobileShowsProjects ? t("nav.projects") : t("nav.chat")}</div></div>
              <button type="button" aria-label={t("common.close")} onClick={() => setMobileSidebar(false)} className="tap grid place-items-center rounded-md p-1 hover:bg-surface-2"><X className="size-5" /></button>
            </div>
            <div className="min-h-0 flex-1">
              {mobileShowsProjects ? <ProjectsSidebar onNavigate={() => setMobileSidebar(false)} onNewProject={() => { setMobileSidebar(false); setCreating(true); }} /> : <SpaceSidebar onNavigate={() => setMobileSidebar(false)} />}
            </div>
          </div>
          <button type="button" aria-label={t("common.close")} className="flex-1 bg-black/50" onClick={() => setMobileSidebar(false)} />
        </div>
      )}

      {/* main */}
      <main className="min-h-0 min-w-0 flex-1 overflow-hidden pt-safe md:pt-0">
        <Outlet />
      </main>

      {/* bottom bar (phone) */}
      <nav className="flex shrink-0 items-center justify-around border-t border-border bg-surface px-2 py-1 md:hidden" style={{ paddingBottom: "var(--nav-pb, max(0.25rem, env(safe-area-inset-bottom)))" }}>
        {can("containers") && <Link to="/" activeOptions={{ exact: true }} className="grid size-11 place-items-center rounded-md text-fg-muted" activeProps={{ className: "bg-accent-soft text-fg" }} aria-label={t("nav.home")}><LogoMark size={26} /></Link>}
        {can("containers") && (
          <button type="button" onClick={() => { if (mobileSidebar && mobileShowsProjects) return setMobileSidebar(false); setMobileProjects(true); setMobileSidebar(true); }} className={cn("relative grid size-11 place-items-center rounded-md text-fg-muted", mobileSidebar && mobileShowsProjects && "bg-accent-soft text-fg")} aria-label={t("nav.containers")}>
            <Monitor className="size-5" />
            {attention > 0 && <span className="absolute top-1 end-1 grid min-w-[14px] place-items-center rounded-full bg-danger px-1 text-[9px] font-semibold leading-[14px] text-white">{attention}</span>}
          </button>
        )}
        {can("studio") && <Link to="/agents" className="grid size-11 place-items-center rounded-md text-fg-muted" activeProps={{ className: "bg-accent-soft text-fg" }} aria-label={t("nav.agents")}><Bot className="size-5" /></Link>}
        {/* like the computer icon: in the Chat section it opens its conversations list (with spaces, tasks, skills,
            MCP); from elsewhere it opens the section */}
        {can("chat") && <button type="button" onClick={() => { if (!inSpace) { void nav({ to: "/chat" }); return; } setMobileProjects(false); setMobileSidebar(!(mobileSidebar && !mobileShowsProjects)); }} className={cn("grid size-11 place-items-center rounded-md text-fg-muted", inSpace && (mobileSidebar && !mobileShowsProjects ? "bg-accent-soft text-fg" : "text-accent"))} aria-label={t("nav.chat")}><MessageCircle className="size-5" /></button>}
        <Link to="/settings" className="grid size-11 place-items-center rounded-md text-fg-muted" activeProps={{ className: "bg-accent-soft text-fg" }} aria-label={t("nav.settings")}><Settings className="size-5" /></Link>
        <button type="button" onClick={() => void askLogout()} className="grid size-11 place-items-center rounded-md text-fg-muted" aria-label={t("common.signOut")}><LogOut className="size-5" /></button>
      </nav>

      {creating && <CreateProjectDialog onClose={() => setCreating(false)} />}
      {shortcutsOpen && <ShortcutsDialog onClose={() => setShortcutsOpen(false)} />}
      <span className="hidden">{user.email}</span>
    </div>
  );
}

const isApple = () => /Mac|iPhone|iPad|iPod/.test(navigator.platform) || /iPad|iPhone/.test(navigator.userAgent);

function ShortcutsDialog({ onClose }: { onClose: () => void }) {
  const { t } = useTranslation();
  const m = isApple() ? "⌘" : "Ctrl";
  const rows: [string, string][] = [
    [`${m} K`, t("shortcuts.search")],
    [`${m} \\`, t("shortcuts.sidebar")],
    [`${m} Enter`, t("shortcuts.send")],
    [`${m} ,`, t("shortcuts.settings")],
    [`${m} /`, t("shortcuts.help")],
    ["Esc", t("shortcuts.close")],
  ];
  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-black/50 p-4" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div role="dialog" aria-label={t("shortcuts.title")} className="w-full max-w-sm rounded-xl border border-border bg-surface p-4 shadow-xl">
        <div className="mb-3 flex items-center justify-between"><h3 className="font-semibold">{t("shortcuts.title")}</h3><button type="button" aria-label={t("common.close")} onClick={onClose} className="tap grid place-items-center rounded-md text-fg-muted hover:text-fg"><X className="size-4" /></button></div>
        <ul className="space-y-2 text-sm">
          {rows.map(([k, label]) => (
            <li key={k} className="flex items-center justify-between gap-3"><span>{label}</span><kbd dir="ltr" className="rounded border border-border bg-surface-2 px-2 py-0.5 font-mono text-xs">{k}</kbd></li>
          ))}
        </ul>
      </div>
    </div>
  );
}
